import { useCallback, useState } from "react";
import { local } from "@/lib/storage";

/**
 * The review's per-person browser preferences (wiki-collaboration-ux D15): Show issues inline and
 * where the review item docks. Kept in `localStorage` under the person's ID, so two people sharing
 * a browser keep their own; a conveniences-only value, never content. Null until the person
 * chooses: the caller's default applies (it may depend on the set, e.g. whether they decide).
 */
export const reviewPrefKey = (name: "inline" | "dock" | "pending", userId: string) =>
  `esi.wiki.review.${name}.${userId.toLowerCase()}`;

export function useReviewPref<T extends string>(
  name: "inline" | "dock" | "pending",
  userId: string,
  allowed: readonly T[],
): [T | null, (value: T) => void] {
  const key = reviewPrefKey(name, userId);
  const read = () => {
    const stored = local.get(key);
    return (allowed as readonly string[]).includes(stored ?? "") ? (stored as T) : null;
  };
  const [chosen, setChosen] = useState<T | null>(read);
  const set = useCallback(
    (value: T) => {
      local.set(key, value);
      setChosen(value);
    },
    [key],
  );
  return [chosen, set];
}
